import { devLogger } from '@autone/utils';
import {
  getClaims as getWorkosClaims,
  useAuth as useWorkosAuth,
} from '@workos-inc/authkit-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  safeParsePermissionsList,
  safeParsePermissionsMap,
  type User,
} from '../types';

import { useGetParams } from './useGetParams';

type WorkosAuthClaims = {
  autone: {
    customer_id: string;
    user_id: string;
    applications: string;
    permissions: string;
    customer_permissions: string;
    external_customer_id: string;
    external_user_id: string;
  };
};

const unauthenticatedUser: User = {
  applications: [],
  permissions: [],
};

export const useAuthn = (shouldAuthenticateOnMount: boolean = true) => {
  const {
    isLoading: isWorkosLoading,
    getAccessToken: getWorkosAccessToken,
    getUser: getWorkosUser,
    signIn: signInWorkos,
    signOut: signOutWorkos,
  } = useWorkosAuth();

  const [hasCompletedInitialAuthCheck, setHasCompletedInitialAuthCheck] =
    useState(!shouldAuthenticateOnMount);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User>(unauthenticatedUser);

  const params = useGetParams<{ email?: string }>();

  const isLoading = isWorkosLoading || !hasCompletedInitialAuthCheck;

  const authenticateWorkos = useCallback(async (): Promise<User> => {
    const accessToken = await getWorkosAccessToken();

    const {
      autone: {
        customer_id: customerId,
        user_id: userId,
        customer_permissions,
        permissions,
        applications,
      },
    } = getWorkosClaims<WorkosAuthClaims>(accessToken);

    const user = getWorkosUser()!;

    const parsedCustomerPermissions =
      safeParsePermissionsMap(customer_permissions);
    const parsedPermissions = safeParsePermissionsList(permissions);

    return {
      apiKey: accessToken,
      accessKey: accessToken,

      customerId: customerId,
      userId: userId,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,

      applications: JSON.parse(applications),
      permissions:
        parsedCustomerPermissions?.[customerId] ?? parsedPermissions ?? [],

      authProvider: 'workos',
    };
  }, [getWorkosAccessToken, getWorkosUser]);

  const authenticate = useCallback(async () => {
    try {
      const providerUser = await authenticateWorkos();
      const tokenHasChanged = providerUser.apiKey !== user.apiKey;
      setIsAuthenticated(true);

      if (tokenHasChanged) {
        setUser(providerUser);
        setHasCompletedInitialAuthCheck(true);
        return providerUser;
      } else {
        return user;
      }
    } catch (error) {
      devLogger.error('Authentication failed:', error);
      await signInWorkos({ loginHint: params.email });
      setIsAuthenticated(false);
      setHasCompletedInitialAuthCheck(true);
      return unauthenticatedUser;
    }
  }, [authenticateWorkos, params.email, signInWorkos, user]);

  const authenticateUser = useCallback(async () => {
    if (isLoading) {
      devLogger.error('authenticateUser should not be called while loading');
    }
    return await authenticate();
  }, [authenticate, isLoading]);

  const signOut = useCallback(async () => {
    if (isLoading) {
      devLogger.error('signOut should not be called while loading');
    }

    await signOutWorkos({ navigate: false });
    setIsAuthenticated(false);
    setUser(unauthenticatedUser);
  }, [isLoading, signOutWorkos]);

  useEffect(() => {
    if (shouldAuthenticateOnMount && !isWorkosLoading) {
      void authenticate();
    }
  }, [shouldAuthenticateOnMount, isWorkosLoading, authenticate]);

  return useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      authenticateUser,
      user,
      signOut,
    }),
    [isLoading, isAuthenticated, authenticateUser, user, signOut],
  );
};
